import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useFormWithValidation } from '@/shared/lib';
import { useRejectServiceRequest, type ServiceRequestRejectRequest } from '@/entities/service-request';

import {
  createRejectServiceRequestSchema,
  DEFAULT_REJECT_VALUES,
  type RejectServiceRequestFormValues
} from './validation';

interface UseRejectServiceRequestFormProps {
  serviceRequestId: number;
  onClose: () => void;
}

export const useRejectServiceRequestForm = ({ serviceRequestId, onClose }: UseRejectServiceRequestFormProps) => {
  const { t } = useTranslation();

  const schema = createRejectServiceRequestSchema(t);
  const form = useFormWithValidation(schema, DEFAULT_REJECT_VALUES);

  // The list and summary refresh automatically via the reject mutation's invalidatesTags,
  // so no explicit refetch is needed here.
  const { rejectServiceRequest, isLoading } = useRejectServiceRequest({
    onSuccess: () => {
      toast.success(t('cmrlist.reject.success', 'Service request rejected'));
      onClose();
    },
    onError: () => {
      toast.error(t('cmrlist.reject.error', 'Failed to reject service request'));
    }
  });

  // Reset the form whenever the targeted request changes so a freshly opened modal starts blank.
  useEffect(() => {
    form.reset(DEFAULT_REJECT_VALUES);
  }, [serviceRequestId, form]);

  const onSubmit = useCallback(
    async (values: RejectServiceRequestFormValues) => {
      try {
        await rejectServiceRequest({
          id: serviceRequestId,
          rejectReason: values.rejectReason as ServiceRequestRejectRequest['rejectReason'],
          rejectComment: values.rejectComment
        });
      } catch {
        // Error feedback is handled by the hook's onError callback.
      }
    },
    [rejectServiceRequest, serviceRequestId]
  );

  return {
    form,
    onSubmit: form.handleSubmit(onSubmit),
    isSubmitting: isLoading
  };
};
